import { useCallback, useEffect, useState } from "react";
import { Box, Button, Container, Divider, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import CustomRightImage from "@components/CustomRightImagev2";
import { InactivityHandler } from "@components/InactivityHandler";
import { RootState, useAppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import { useLazyGetLoanApplicationByIdQuery, usePostValidateNextStageMutation } from "@api/applications";
import { useNavigate } from "react-router-dom";
import useStatusConfig from "@hooks/useStatusConfig";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  ApiError,
  BofuFormsPF,
  BofuTabDocument,
  DocumentResponse,
  FORM_STATUS_ENUM,
  PersonaStructure,
} from "@interfaces/response";
import * as Sentry from "@sentry/react";
import ButtonExpedienteStatus from "@components/ButtonExpedienteStatus";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import { UUID } from "crypto";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import { BOFU_TABS_CONFIG, BOFU_TABS_FORM_CODES, DOCUMENTS_CATALOG } from "@helpers/constantes";
import { setBofuTabEmpresaForms, setParamsIdentityValidationForm } from "@store/slices/bofuSlice";
import { useLazyGetTabsConfigQuery } from "@api/catalogs";
import {
  generateDocumentConfig,
  generatePropietarioDocs,
  mapBofuTabEmpresaForms,
  validateDocumentStructure,
} from "@components/TabBofu/functions";
import DocumentsTabBofu, { ValueAction } from "@components/TabBofu/DocumentsTabBofu";
import CenteredLoader from "@components/CenteredLoader";
import { setLoanApplication } from "@store/slices/registerSlice";

const SCREEN_STATUS = "CSAED";

const CredimotoExpediente = () => {
  // hooks
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);

  // redux hooks
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // api hooks
  const [getLoanApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getPersonsByLoandId] = useLazyGetPersonsQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();

  // state
  const [legalRepresentative, setLegalRepresentative] = useState<PersonaStructure | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isBofuCompleted, setIsBofuCompleted] = useState(false);
  const [docValidacionIdentidad, setDocValidacionIdentidad] = useState<DocumentResponse | undefined>(undefined);
  const [expedienteDocuments, setExpedienteDocuments] = useState<BofuTabDocument[]>([]);
  const [availableForms, setAvailableForms] = useState<BofuFormsPF>({
    validacion_identidad: false,
    referencias_personales: false,
    solicitud_credito: false,
    informacion_bancaria: false,
  });

  const handleClickSolicitudCredito = () => {
    navigate("/producto/creditosimple/solicitud_credito_1PF");
  };

  const handleClickReferenciasPersonales = () => {
    navigate("/producto/creditosimple/referencias_personales");
  };

  const handleClickInformacionBancaria = () => {
    navigate("/producto/creditosimple/informacion_bancaria_pf");
  };

  const handleClickIdentityValidation = () => {
    if (!legalRepresentative?.id) {
      return;
    }

    dispatch(
      setParamsIdentityValidationForm({
        person_id: legalRepresentative?.id,
        person_type: "REP_LEGAL_PF",
      }),
    );
    setTimeout(() => {
      navigate("/producto/creditosimple/validacion_identidad_pf");
    }, 200);
  };

  const handleContinue = async () => {
    try {
      if (!application.id) {
        throw new Error("Application ID is required");
      }

      if (!nextStatus) {
        throw new Error("Next status is required");
      }

      if (!isBofuCompleted) {
        throw new Error("Bofu is not completed");
      }

      setIsLoading(true);

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      });

      navigate(nextStatus.url);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "ExpedientePF-handleContinue",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateDocument = (document: BofuTabDocument, newValue: DocumentResponse | undefined) => {
    const copy = [...expedienteDocuments];
    const documentIndex = copy.findIndex((doc) => doc.file_category_code === document.file_category_code);
    if (documentIndex !== -1) {
      copy[documentIndex].file_value = newValue;
    }
    setExpedienteDocuments(copy);
  };

  const handleUpdateDocumentMultiple = (
    document: BofuTabDocument,
    newValue: DocumentResponse | undefined,
    valueAction: ValueAction | undefined,
    valueIndex: number | undefined,
  ) => {
    if (valueAction === "add") {
      const copy = [...expedienteDocuments];
      const documentIndex = copy.findIndex((doc) => doc.file_category_code === document.file_category_code);
      if (documentIndex !== -1) {
        copy[documentIndex].list_values.push(newValue);
      }
      setExpedienteDocuments(copy);
    } else if (valueAction === "remove") {
      const copy = [...expedienteDocuments];
      const documentIndex = copy.findIndex((doc) => doc.file_category_code === document.file_category_code);
      if (documentIndex !== -1) {
        copy[documentIndex].list_values.splice(valueIndex as number, 1);
      }
      setExpedienteDocuments(copy);
    } else if (valueAction === "update") {
      const copy = [...expedienteDocuments];
      const documentIndex = copy.findIndex((doc) => doc.file_category_code === document.file_category_code);
      if (documentIndex !== -1) {
        copy[documentIndex].list_values[valueIndex as number] = newValue;
      }
      setExpedienteDocuments(copy);
    }
  };

  const validateAllDocumentsAreUploaded = useCallback(() => {
    return expedienteDocuments.every((doc) => doc.file_value !== undefined);
  }, [expedienteDocuments]);

  const validateAllFormsAreCompleted = useCallback(() => {
    const forms: FORM_STATUS_ENUM[] = [];
    if (availableForms.solicitud_credito) {
      forms.push(application.loan_application_information_status);
    }

    if (availableForms.referencias_personales) {
      forms.push(application.references_status);
    }

    if (availableForms.informacion_bancaria) {
      forms.push(application.bank_information_status);
    }

    if (availableForms.validacion_identidad) {
      forms.push(legalRepresentative?.identity_validation_completed === true ? "COMPLETO" : "PENDIENTE");
      forms.push(docValidacionIdentidad ? "COMPLETO" : "PENDIENTE");
    }

    return forms.every((form) => form === "COMPLETO" || form === "VALIDADO");
  }, [
    application.bank_information_status,
    application.loan_application_information_status,
    application.references_status,
    availableForms.informacion_bancaria,
    availableForms.referencias_personales,
    availableForms.solicitud_credito,
    availableForms.validacion_identidad,
    docValidacionIdentidad,
    legalRepresentative?.identity_validation_completed,
  ]);

  const fetchInitialData = useCallback(async () => {
    try {
      if (!application.id || !application.client_details_id) {
        throw new Error("Application not found");
      }

      setIsLoading(true);

      const [loanApplicationResponse, persons, tabsConfig, documents] = await Promise.all([
        getLoanApplicationById(application.id).unwrap(),
        getPersonsByLoandId({ loanApplicationId: application.id }).unwrap(),
        getTabsConfig({ taxcode: application.type }).unwrap(),
        getDocumentsByAppId({ loanApplicationId: application.id, status: true }).unwrap(),
      ]);

      if (!loanApplicationResponse) {
        throw new Error("Loan application not found");
      }

      if (persons.length === 0) {
        throw new Error("Persons not found");
      }

      const tab_representante_pf = tabsConfig.find(
        (tab) => tab.tabs_bofu.code === BOFU_TABS_CONFIG.tab_representante_pf.code,
      );

      const valid_tab_documents: BofuTabDocument[] = [];

      tab_representante_pf?.documents
        .filter((item) => validateDocumentStructure(item))
        .forEach((docItem) => {
          const document_value = generateDocumentConfig({
            docItem,
            docs_account: [],
            docs_aval: [],
            clientDetailsId: application.client_details_id as UUID,
            aval: undefined,
          });

          valid_tab_documents.push(document_value);
        });

      // identify documents
      const docs_person = documents.filter((doc) => doc.person_id === persons[0].id);
      const doc_validacion_identidad = docs_person.find(
        (doc) => doc.file_code === DOCUMENTS_CATALOG.validacion_identidad.code,
      );

      const representante_documents = generatePropietarioDocs({
        personItem: persons[0],
        valid_documents: valid_tab_documents,
        documents_uploaded: docs_person,
        clientDetailsId: application.client_details_id as UUID,
      });

      // identify forms
      const forms_config = tab_representante_pf?.forms || [];
      const solicitud_credito = forms_config.find((item) => item.code === BOFU_TABS_FORM_CODES.solicitud_credito);
      const validacion_identidad_form = forms_config.find(
        (item) => item.code === BOFU_TABS_FORM_CODES.validacion_identidad,
      );
      const referencias_personales = forms_config.find(
        (item) => item.code === BOFU_TABS_FORM_CODES.referencias_personales,
      );
      const informacion_bancaria = forms_config.find((item) => item.code === BOFU_TABS_FORM_CODES.informacion_bancaria);

      // set available forms
      setAvailableForms({
        solicitud_credito: solicitud_credito ? true : false,
        validacion_identidad: validacion_identidad_form ? true : false,
        referencias_personales: referencias_personales ? true : false,
        informacion_bancaria: informacion_bancaria ? true : false,
      });

      // TabBofu (PM/PFAE) es el que normalmente llena bofu_tabs_obj.empresa.forms con la
      // config real del API (para useSolicitudCreditoForm). PF no pasa por ese componente,
      // así que se guarda aquí directamente para que los steps de solicitud_credito PF
      // también reciban la config dinámica en vez de quedarse siempre en los defaults.
      dispatch(setBofuTabEmpresaForms(mapBofuTabEmpresaForms(forms_config)));

      setExpedienteDocuments(representante_documents);
      setDocValidacionIdentidad(doc_validacion_identidad);
      setLegalRepresentative(persons[0]);
      setIsBofuCompleted(false);
      dispatch(setLoanApplication(loanApplicationResponse));
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "ExpedientePF-fetchInitialData",
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    application.client_details_id,
    application.id,
    application.type,
    dispatch,
    getDocumentsByAppId,
    getLoanApplicationById,
    getPersonsByLoandId,
    getTabsConfig,
  ]);

  useEffect(() => {
    const all_documents_uploaded = validateAllDocumentsAreUploaded();
    const all_forms_completed = validateAllFormsAreCompleted();
    const is_bofu_completed = all_documents_uploaded && all_forms_completed;
    setIsBofuCompleted(is_bofu_completed);
  }, [validateAllDocumentsAreUploaded, validateAllFormsAreCompleted]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        {isLoading ? (
          <CenteredLoader />
        ) : (
          <Box
            sx={{
              mt: 2,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography variant={"tofu_title"} textAlign="center" color={"primary.dark"}>
              Expediente
            </Typography>

            <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 3 : 4, textAlign: "center" }}>
              {availableForms.solicitud_credito && (
                <>
                  <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="body1"
                      sx={{ flex: 1, textAlign: "left" }}
                      color={theme?.palette?.primary?.dark}
                    >
                      Solicitud de crédito
                    </Typography>

                    <ButtonExpedienteStatus
                      mode="form"
                      formStatus={application?.loan_application_information_status}
                      onClick={handleClickSolicitudCredito}
                    />
                  </Box>
                  <Divider sx={{ backgroundColor: "#A3D4E8", mt: 2 }} />
                </>
              )}

              {availableForms.validacion_identidad && (
                <>
                  <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="body1"
                      sx={{ flex: 1, textAlign: "left" }}
                      color={theme?.palette?.primary?.dark}
                    >
                      Validación de identidad
                    </Typography>

                    <ButtonExpedienteStatus
                      mode="validation"
                      isCompleted={legalRepresentative?.identity_validation_completed}
                      documentStatus={docValidacionIdentidad?.validated}
                      onClick={() => handleClickIdentityValidation()}
                    />
                  </Box>
                  <Divider sx={{ backgroundColor: "#A3D4E8", mt: 2 }} />
                </>
              )}

              {availableForms.referencias_personales && (
                <>
                  <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="body1"
                      sx={{ flex: 1, textAlign: "left" }}
                      color={theme?.palette?.primary?.dark}
                    >
                      Referencias personales
                    </Typography>

                    <ButtonExpedienteStatus
                      mode="form"
                      formStatus={application?.references_status}
                      onClick={handleClickReferenciasPersonales}
                    />
                  </Box>
                  <Divider sx={{ backgroundColor: "#A3D4E8", mt: 2 }} />
                </>
              )}

              {availableForms.informacion_bancaria && (
                <>
                  <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="body1"
                      sx={{ flex: 1, textAlign: "left" }}
                      color={theme?.palette?.primary?.dark}
                    >
                      Información bancaria
                    </Typography>

                    <ButtonExpedienteStatus
                      mode="form"
                      formStatus={application?.bank_information_status}
                      onClick={handleClickInformacionBancaria}
                    />
                  </Box>
                  <Divider sx={{ backgroundColor: "#A3D4E8", mt: 2 }} />
                </>
              )}

              <Box mt={2} mb={2} display="flex" justifyContent="center">
                <Typography
                  mt={2}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  sx={{ color: theme?.palette?.primary?.dark, fontSize: "16px", fontWeight: 600 }}
                >
                  Ahora carga los siguientes documentos
                </Typography>
              </Box>

              <DocumentsTabBofu
                inputPrefix={`representante_legal`}
                tabCode={BOFU_TABS_CONFIG.tab_representante_pf.code}
                documents={expedienteDocuments}
                onChange={({ document, newValue }) => handleUpdateDocument(document, newValue)}
                onChangeMultiple={({ document, newValue, valueAction, valueIndex }) =>
                  handleUpdateDocumentMultiple(document, newValue, valueAction, valueIndex)
                }
                RFC={legalRepresentative?.rfc}
              />

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button
                  type="submit"
                  variant="default"
                  disabled={isLoading || expedienteDocuments.length === 0 || !isBofuCompleted}
                  onClick={handleContinue}
                >
                  Continuar
                </Button>
              </Box>
            </Container>
          </Box>
        )}
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default CredimotoExpediente;
